import { useCallback, useEffect, useState } from "react";
import * as yup from "yup";

import { useOptions } from "@/api/options";
import { useFormStore } from "@/store/form";

// Components
import Column from "@/components/Column";
import Input from "@/components/Input";
import RadioBox from "@/components/RadioBox";
import Select from "@/components/Select";
import SelectItem from "@/components/Select/Item";
import Text from "@/components/Text";

// Icon
// import BankIcon from "@/components/Icons/Bank";

// Api
import { sendPaymentUserJourney } from "@/api/userJourney";
import RadioBoxOption from "@/components/RadioBoxOption";
import { calculateIdealDueDate } from "@/utils/idealDueDates";

// Schema
export const autoDebtDataSchema = yup.object().shape({
  payment: yup.object().shape({
    bank: yup.string().required("O banco é obrigatório"),
    agency: yup.string().required("A agência é obrigatória"),
    account: yup.string().required("A conta é obrigatória"),
    debtDueDate: yup.string().required("A data de vencimento é obrigatória"),
    knownClausules: yup.bool().oneOf([true], "Você deve aceitar o contrato"),
    knownTerms: yup.bool().oneOf([true], "Você deve aceitar os termos"),
  }),
});

export const invoiceSchema = yup.object().shape({
  payment: yup.object().shape({
    debtDueDate: yup.string().required("A data de vencimento é obrigatória"),
    knownClausules: yup.bool().oneOf([true], "Você deve aceitar o contrato"),
    knownTerms: yup.bool().oneOf([true], "Você deve aceitar os termos"),
  }),
});

// Automatic Debt Payment Fields
export type AutoDebtPaymentFieldsProps = {
  hasPayment?: boolean;
  setFormIsValid(isValid: boolean): void;
};

const AutoDebtPaymentFields = ({
  hasPayment,
  setFormIsValid,
}: AutoDebtPaymentFieldsProps) => {
  const { result: data } = useOptions();
  const payment = useFormStore((store) => store.value.payment);
  const uuid = useFormStore((store) => store.value.uuid);
  const addData = useFormStore((store) => store.addData);
  const [debtDueDate, setDebtDueDate] = useState(() => {
    if (payment.debtDueDate) {
      return String(payment.debtDueDate);
    }

    const today = new Date();
    const currentDay = today.getDate();

    return String(calculateIdealDueDate(currentDay));
  });

  const [selectedBank, setSelectedBank] = useState(payment.bank);
  const [agency, setAgency] = useState(payment.agency);
  const [account, setAccount] = useState(payment.account);

  useEffect(() => {
    const formDebitIsValid = hasPayment
      ? !!(selectedBank && agency && account && debtDueDate)
      : !!debtDueDate;

    setFormIsValid(formDebitIsValid);
  }, [debtDueDate, setFormIsValid, selectedBank, agency, account, hasPayment]);

  const handleOnChangepaymentTypeUserJourney = useCallback(
    (value: string) => {
      addData({
        payment: {
          ...payment,
          debtDueDate: value,
        },
      });
      sendPaymentUserJourney({
        ...payment,
        ...{
          debtDueDate: value,
          passo: "PAGAMENTO",
          uuid,
        },
      });
    },
    [addData, payment, uuid]
  );

  return (
    <>
      {hasPayment && (
        <>
          <Column size={12} className="pb-6">
            <Select
              // icon={BankIcon}
              value={selectedBank || ""}
              name="payment.bank"
              placeholder="Banco"
              onChange={(e) => setSelectedBank(e.target.value)}
              onBlur={(e) => setSelectedBank(e.target.value)}
            >
              {data?.banks?.map((bank) => (
                <SelectItem key={bank.value} value={bank.value}>
                  {bank.label}
                </SelectItem>
              ))}
            </Select>
          </Column>

          <Column size={12} className="pb-6">
            <Input
              type="tel"
              mask={"9".repeat(10)}
              name="payment.agency"
              placeholder="Agência"
              value={agency}
              onChange={(e) => setAgency(e.target.value)}
              onBlur={(e) => setAgency(e.target.value)}
            />
          </Column>

          <Column size={12} className="pb-6">
            <Input
              type="tel"
              mask={"9".repeat(10)}
              name="payment.account"
              placeholder="Conta corrente"
              value={account}
              onChange={(e) => setAccount(e.target.value)}
              onBlur={(e) => setAccount(e.target.value)}
            />
          </Column>
        </>
      )}

      <Column size={12} className="pb-6">
        <Text as="p" kind="blue" className="pb-3 font-medium leading-5">
          Escolha a data de vencimento
        </Text>

        <RadioBox
          name="payment.debtDueDate"
          value={debtDueDate}
          onChange={(e) => {
            setDebtDueDate(String(e.target.value));
            handleOnChangepaymentTypeUserJourney(e.target.value);
          }}
        >
          {data?.dueDates?.map((dueDate) => (
            <RadioBoxOption
              key={String(dueDate.value)}
              value={String(dueDate.value)}
            >
              {dueDate.label}
            </RadioBoxOption>
          ))}
        </RadioBox>
      </Column>
    </>
  );
};

export default AutoDebtPaymentFields;
